import { useCallback, useEffect, useMemo, useState } from "react";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Cell,
    AreaChart,
    Area,
    ComposedChart,
    Line,
} from "recharts";
import { BarChart3, CalendarClock, ArrowUpRight, ArrowDownRight, Clock3 } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { EmptyState } from "@/components/common/EmptyState";
import { OPS } from "@/constants/testIds";
import { fmtNumber, fmtCompact, currencySuffix } from "@/lib/format";

const S = OPS.sales;

const RAG_FILL = {
    green: "var(--success)",
    amber: "var(--brand-orange)",
    red: "var(--danger)",
};
const NEUTRAL_FILL = "var(--brand-sky)";

function DeltaPill({ v, lang }) {
    if (v == null) return <span className="text-[color:var(--ink-faint)]">—</span>;
    const up = v >= 0;
    const Icon = up ? ArrowUpRight : ArrowDownRight;
    return (
        <span
            className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold tnum ${
                up
                    ? "bg-[color:rgba(31,122,77,0.12)] text-[color:var(--success)]"
                    : "bg-[color:rgba(220,38,38,0.10)] text-[color:var(--danger)]"
            }`}
        >
            <Icon className="h-3 w-3" />
            {up ? "+" : ""}{fmtNumber(v, lang, 1)}%
        </span>
    );
}

function CompareTooltip({ active, payload, lang, t }) {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
        <div className="rounded-xl border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3 shadow-[var(--shadow-md)] text-xs min-w-[180px]">
            <p className="font-semibold text-[color:var(--ink)] mb-1.5">{d.name}</p>
            <div className="flex items-center justify-between gap-4">
                <span className="text-[color:var(--ink-muted)]">{t("sc_col_revenue")}</span>
                <span className="tnum font-semibold text-[color:var(--ink)]">{fmtNumber(d.revenue, lang, 0)}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
                <span className="text-[color:var(--ink-muted)]">{t("sc_col_target")}</span>
                <span className="tnum text-[color:var(--ink)]">
                    {d.target != null ? fmtNumber(d.target, lang, 0) : t("sc_no_target")}
                </span>
            </div>
            {d.attainment != null ? (
                <div className="flex items-center justify-between gap-4">
                    <span className="text-[color:var(--ink-muted)]">{t("sc_attainment")}</span>
                    <span className="tnum font-semibold" style={{ color: RAG_FILL[d.rag] || "var(--ink)" }}>
                        {fmtNumber(d.attainment, lang, 0)}%
                    </span>
                </div>
            ) : null}
            <div className="flex items-center justify-between gap-4 mt-1 pt-1 border-t border-[color:var(--separator)]">
                <span className="text-[color:var(--ink-muted)]">{t("sc_vs_prev")}</span>
                <DeltaPill v={d.mom} lang={lang} />
            </div>
        </div>
    );
}

function TrendTooltip({ active, payload, lang, t }) {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
        <div className="rounded-xl border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3 shadow-[var(--shadow-md)] text-xs">
            <p className="font-semibold text-[color:var(--ink)] mb-1">{d.name}</p>
            <div className="flex items-center justify-between gap-4">
                <span className="text-[color:var(--ink-muted)]">{t("sc_col_revenue")}</span>
                <span className="tnum font-semibold text-[color:var(--ink)]">
                    {d.revenue != null ? fmtNumber(d.revenue, lang, 0) : "—"}
                </span>
            </div>
            {d.partial ? (
                // month-to-date: a part of a month is never compared with a whole one
                <p data-testid={`${S.scorecardTrendChart}-tip-through`} className="mt-1 text-[11px] text-[color:var(--ink-muted)]">
                    {t("sc_mtd_through")} <bdi dir="ltr">{d.through || "—"}</bdi>
                </p>
            ) : d.mom != null ? (
                <div className="flex items-center justify-end mt-1"><DeltaPill v={d.mom} lang={lang} /></div>
            ) : null}
        </div>
    );
}

export default function SalesScorecard({ meta }) {
    const { t, lang, tn } = useI18n();
    const [month, setMonth] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // default month from coverage (latest month with data)
    useEffect(() => {
        if (month || !meta?.coverage_to) return;
        setMonth(meta.coverage_to.slice(0, 7));
    }, [meta, month]);

    const load = useCallback(async (m) => {
        setLoading(true);
        try {
            const r = await api.get("/sales/branch-scorecard", { params: { month: m } });
            setData(r.data);
            setError(null);
        } catch (e) {
            setError(e?.response?.data?.detail || t("error_generic"));
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        if (month) load(month);
    }, [month, load]);

    const branches = data?.branches || [];
    const nm = (b) => tn({ name_ar: b.branch.name_ar, name_en: b.branch.name_en });

    // Compare: has-data branches, sorted by revenue DESCENDING (biggest first);
    // RAG color flags under/over-target branches at a glance.
    const compareData = useMemo(() => {
        return branches
            .filter((b) => b.has_data)
            .map((b) => ({
                id: b.branch.id,
                name: nm(b),
                revenue: b.revenue,
                target: b.target,
                attainment: b.achievement_pct,
                mom: b.mom_delta_pct,
                rag: b.rag,
                fill: b.rag ? RAG_FILL[b.rag] : NEUTRAL_FILL,
            }))
            .sort((a, b) => (b.revenue ?? 0) - (a.revenue ?? 0));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [branches, lang]);

    // No month-on-month for a month-to-date point, nor against one: October so far
    // against all of September read as a red −38%.
    const trendData = useMemo(
        () => (data?.series || []).map((s) => ({
            month: s.month,
            name: lang === "ar" ? s.label_ar : s.label_en,
            revenue: s.revenue,
            partial: !!s.partial,
            through: s.through || null,
        })).map((row, i, arr) => ({
            ...row,
            mom: i > 0 && !row.partial && !arr[i - 1].partial && row.revenue != null && arr[i - 1].revenue
                ? Math.round((row.revenue - arr[i - 1].revenue) / Math.abs(arr[i - 1].revenue) * 1000) / 10 : null,
        })),
        [data, lang]
    );

    const yoyData = useMemo(
        () => branches.filter((b) => b.has_data).map((b) => ({
            id: b.branch.id,
            name: nm(b),
            current: b.revenue,
            prior: b.last_year_revenue,
        })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [branches, lang]
    );

    const compareHeight = Math.max(220, compareData.length * 34 + 24);
    const suffix = currencySuffix(lang);

    const monthOptions = data?.months_available?.length
        ? [...data.months_available].reverse()
        : month ? [month] : [];
    const monthLabel = (m) => {
        const lbl = data?.month_labels?.[m];
        if (lbl) return lang === "ar" ? lbl.ar : lbl.en;
        const hit = (data?.series || []).find((s) => s.month === m);
        if (hit) return lang === "ar" ? hit.label_ar : hit.label_en;
        if (m === data?.month) return lang === "ar" ? data.month_label_ar : data.month_label_en;
        return m;
    };

    return (
        <Card
            data-testid={S.scorecard}
            className="rounded-2xl border-[color:var(--border-soft)] shadow-[var(--shadow-sm)] overflow-hidden"
        >
            <CardContent className="p-5 sm:p-6">
                {/* header */}
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2.5">
                        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[color:var(--brand-orange-soft)]">
                            <BarChart3 className="h-5 w-5 text-[color:var(--brand-orange)]" />
                        </span>
                        <div>
                            <h3 className="text-base font-bold text-[color:var(--ink)]">{t("sc_title")}</h3>
                            <p className="text-xs text-[color:var(--ink-muted)] max-w-md">{t("sc_subtitle")}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <CalendarClock className="h-3.5 w-3.5 text-[color:var(--ink-faint)]" />
                        <Select value={month || ""} onValueChange={setMonth}>
                            <SelectTrigger data-testid={S.scorecardMonth} className="h-9 w-[150px]">
                                <SelectValue placeholder={t("sc_month")} />
                            </SelectTrigger>
                            <SelectContent>
                                {monthOptions.map((m) => (
                                    <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {data?.month_partial ? (
                    <div
                        data-testid={`${S.scorecard}-mtd`}
                        className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)] px-3 py-2 text-xs"
                    >
                        <Clock3 className="h-3.5 w-3.5 text-[color:var(--ink-faint)]" />
                        <span className="font-semibold text-[color:var(--ink-muted)]">
                            {t("sc_mtd_through")} <bdi dir="ltr">{data.month_through}</bdi>
                        </span>
                        <span className="text-[color:var(--ink-faint)]">· {t("sc_mtd_note")}</span>
                    </div>
                ) : null}

                {error ? (
                    <div className="rounded-lg border border-[color:var(--danger)] bg-[color:rgba(220,38,38,0.06)] p-3 text-sm text-[color:var(--danger)]">{error}</div>
                ) : loading || !data ? (
                    <Skeleton className="h-64 w-full rounded-xl" />
                ) : (
                    <Tabs defaultValue="compare" className="w-full">
                        <TabsList className="mb-4">
                            <TabsTrigger data-testid={S.scorecardTab("compare")} value="compare">{t("sc_tab_compare")}</TabsTrigger>
                            <TabsTrigger data-testid={S.scorecardTab("trend")} value="trend">{t("sc_tab_trend")}</TabsTrigger>
                            <TabsTrigger data-testid={S.scorecardTab("yoy")} value="yoy">{t("sc_tab_yoy")}</TabsTrigger>
                        </TabsList>

                        {/* ---- Branch comparison (underperformers first) ---- */}
                        <TabsContent value="compare">
                            {compareData.length === 0 ? (
                                <EmptyState compact title={t("sc_no_data")} testId={`${S.scorecardCompareChart}-empty`} dataset="sales_daily_branch" month={month} />
                            ) : (
                                <>
                                    <div className="flex flex-wrap items-center gap-3 mb-2 text-[11px] text-[color:var(--ink-muted)]">
                                        {["red", "amber", "green"].map((k) => (
                                            <span key={k} className="inline-flex items-center gap-1.5">
                                                <span className="h-2.5 w-2.5 rounded-full" style={{ background: RAG_FILL[k] }} />
                                                {t(`budget_rag_${k}`)}
                                            </span>
                                        ))}
                                        <span className="inline-flex items-center gap-1.5">
                                            <span className="h-2.5 w-2.5 rounded-full" style={{ background: NEUTRAL_FILL }} />
                                            {t("sc_no_target")}
                                        </span>
                                    </div>
                                    <div dir="ltr" data-testid={S.scorecardCompareChart} style={{ width: "100%", height: compareHeight }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={compareData} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--separator)" />
                                                <XAxis type="number" tickFormatter={(v) => fmtCompact(v)} tick={{ fontSize: 11, fill: "var(--ink-faint)" }} />
                                                <YAxis type="category" dataKey="name" width={lang === "ar" ? 130 : 120} tick={{ fontSize: 11, fill: "var(--ink-muted)" }} interval={0} />
                                                <Tooltip cursor={{ fill: "rgba(224,222,217,0.35)" }} content={<CompareTooltip lang={lang} t={t} />} />
                                                <Bar dataKey="revenue" radius={[0, 5, 5, 0]} maxBarSize={22} isAnimationActive>
                                                    {compareData.map((d) => (
                                                        <Cell key={d.id} fill={d.fill} />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <p className="mt-1 text-[11px] text-[color:var(--ink-faint)] text-center">{suffix}</p>
                                </>
                            )}
                        </TabsContent>

                        {/* ---- Monthly trend (network) ---- */}
                        <TabsContent value="trend">
                            {trendData.filter((d) => d.revenue != null).length < 2 ? (
                                <EmptyState compact title={t("sc_no_data")} testId={`${S.scorecardTrendChart}-empty`} dataset="sales_daily_branch" month={month} />
                            ) : (
                                <div dir="ltr" data-testid={S.scorecardTrendChart} style={{ width: "100%", height: 300 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={trendData} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
                                            <defs>
                                                <linearGradient id="scTrendFill" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="var(--brand-orange)" stopOpacity={0.35} />
                                                    <stop offset="100%" stopColor="var(--brand-orange)" stopOpacity={0.02} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--separator)" />
                                            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--ink-muted)" }} />
                                            <YAxis tickFormatter={(v) => fmtCompact(v)} tick={{ fontSize: 11, fill: "var(--ink-faint)" }} width={52} />
                                            <Tooltip content={<TrendTooltip lang={lang} t={t} />} />
                                            <Area
                                                type="monotone"
                                                dataKey="revenue"
                                                stroke="var(--brand-orange)"
                                                strokeWidth={2.5}
                                                fill="url(#scTrendFill)"
                                                connectNulls={false}
                                                dot={{ r: 3, fill: "var(--brand-orange)" }}
                                                activeDot={{ r: 5 }}
                                                isAnimationActive
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </TabsContent>

                        {/* ---- Year over year ---- */}
                        <TabsContent value="yoy">
                            {!data.yoy_available ? (
                                <EmptyState
                                    icon={CalendarClock}
                                    testId={S.scorecardYoyEmpty}
                                    title={t("sc_yoy_unavailable_title")}
                                    hint={t("sc_yoy_unavailable_desc")}
                                />
                            ) : (
                                <div dir="ltr" data-testid={S.scorecardYoyChart} style={{ width: "100%", height: Math.max(260, yoyData.length * 34) }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart data={yoyData} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--separator)" />
                                            <XAxis type="number" tickFormatter={(v) => fmtCompact(v)} tick={{ fontSize: 11, fill: "var(--ink-faint)" }} />
                                            <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: "var(--ink-muted)" }} interval={0} />
                                            <Tooltip
                                                formatter={(v, n) => [fmtNumber(v, lang, 0), n === "current" ? t("sc_current_year") : t("sc_prior_year")]}
                                                contentStyle={{ borderRadius: 12, border: "1px solid var(--border-soft)", background: "var(--bg-surface)" }}
                                            />
                                            <Bar dataKey="prior" fill="var(--brand-sky)" radius={[0, 5, 5, 0]} maxBarSize={12} name="prior" />
                                            <Bar dataKey="current" fill="var(--brand-orange)" radius={[0, 5, 5, 0]} maxBarSize={12} name="current" />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </TabsContent>
                    </Tabs>
                )}
            </CardContent>
        </Card>
    );
}
